<Container
  x={smoothX}
  y={smoothY}
  zIndex={z}
  controls
  onBeforeDestroy
  visible
  cursor={interactionCursor}
  pointerover={interactionPointerOver}
  pointerout={interactionPointerOut}
  pointerdown={interactionPointerDown}
  pointerup={interactionPointerUp}
  pointermove={interactionPointerMove}
  click={interactionClick}
>
  @for (compConfig of normalizedComponentsBehind) {
    <Container>
      <compConfig.component object={sprite} ...compConfig.props />
    </Container>
  } 
  <PlayerComponents object={sprite} position="bottom" graphicBounds />
  <PlayerComponents object={sprite} position="left" graphicBounds />
  <Particle emit={emitParticleTrigger} settings={particleSettings} zIndex={1000} name={particleName} />
  <Container>
    @for (graphicObj of renderedGraphics) {
      <Container scale={graphicContainerScale(graphicObj)}>
        <Sprite 
          sheet={sheet(graphicObj)} 
          direction 
          tint 
          hitbox={graphicHitbox(graphicObj)}
          shadowCaster={shadowCaster(graphicObj)}
          flash={flashConfig}
        />
      </Container>
    }
    @for (eventComponent of resolvedEventComponents) {
      <Container dependencies={eventComponent.dependencies}>
        <eventComponent.component ...eventComponent.props />
      </Container>
    }
  </Container>
  <PlayerComponents object={sprite} position="center" graphicBounds />
  <PlayerComponents object={sprite} position="right" graphicBounds />
  <PlayerComponents object={sprite} position="top" graphicBounds />
  @for (compConfig of normalizedComponentsInFront) {
    <Container dependencies={compConfig.dependencies}>
      <compConfig.component object={sprite} ...compConfig.props />
    </Container>
  }
  <InteractionComponents
    object={sprite}
    bounds={graphicBounds}
    hitboxBounds={hitboxBounds}
    graphicBounds={graphicBounds}
  />
  @for (attachedGui of attachedGuis) {
    @if (shouldDisplayAttachedGui) {
      <Container>
        <attachedGui.component ...attachedGui.data() dependencies={attachedGui.dependencies} object={sprite} guiOpenId={attachedGui.openId} onFinish={(data, guiOpenId) => {
          onAttachedGuiFinish(attachedGui, data, guiOpenId)
        }} onInteraction={(name, data) => {
          onAttachedGuiInteraction(attachedGui, name, data)
        }} />
      </Container>
    }
  }
</Container>

<script>
  import { signal, effect, mount, computed, tick, animatedSignal, on, untracked } from "canvasengine";
  import { movementPreferences } from "../services/movementPreferences";
  import { Assets } from "pixi.js";
  import { observeCurrentPlayerControls } from "./current-player-controls";
  import type { Subscription } from "rxjs";

  import { lastValueFrom, combineLatest, map, startWith } from "rxjs";
  import { Particle } from "@canvasengine/presets";
  import { GameEngineToken, ModulesToken, shouldRenderLightingShadows } from "@rpgjs/common";
  import { RpgClientEngine } from "../RpgClientEngine";
  import {
    applyCameraFollow,
    clearCameraFollowPlugins,
    ownsCameraFollow,
    ownsCameraFollowRevision,
    syncCameraFollowPosition,
  } from "../services/cameraFollow";
  import { resolveScaledGraphicBounds, resolveScaledHitboxAnchor, scaleHitboxForGraphicDisplay, toPositiveNumber } from "./character-hitbox";
  import { inject } from "../core/inject";  
  import { Direction, Animation } from "@rpgjs/common";
  import { normalizeEventComponent } from "../Game/EventComponentResolver";
  import Hit from "./effects/hit.ce";
  import PlayerComponents from "./player-components.ce";
  import InteractionComponents from "./interaction-components.ce";
  import { RpgGui } from "../Gui/Gui";
  import { getCanMoveValue } from "../utils/readPropValue";
  import { fitAnimationTimeline } from "./animation-timing";
  import { RecoilSmoothing } from "./recoil-smoothing";
  import { resolveCharacterAnimation } from "./character-animation";
  import {
    loadCachedSpriteSheetAlphaBounds,
    mergeSpriteAlphaBounds,
    resolveTextureSourceDimensions,
    scanSpriteSheetAlphaBounds,
  } from "./sprite-alpha-bounds";
  import {
    getKeyboardControlBind,
    keyboardEventMatchesBind,
    resolveKeyboardActionInput,
    resolveKeyboardDirectionInput,
  } from "../services/actionInput";

  const { object, id } = defineProps();
  const sprite = object();

  const client = inject(RpgClientEngine);
  const hooks = inject(ModulesToken);
  const guiService = inject(RpgGui);

  const spritesheets = client.spritesheets;
  const componentsBehind = client.spriteComponentsBehind;
  const componentsInFront = client.spriteComponentsInFront;
  const readProp = (value) => typeof value === 'function' ? value() : value;
  const isCurrentPlayer = () => {
    const playerId = client.playerIdSignal();
    const currentPlayer = playerId ? client.sceneMap?.players?.()?.[playerId] : undefined;
    return readProp(id) === playerId
      || readProp(sprite?.id) === playerId
      || sprite === currentPlayer
      || sprite === client.sceneMap?.getCurrentPlayer?.();
  };
  const isMe = computed(isCurrentPlayer);
  const shadowsEnabled = computed(() => {
    const lighting = client.sceneMap?.lighting?.();
    return shouldRenderLightingShadows(lighting);
  });

  /**
   * Normalize a single sprite component configuration
   * 
   * Handles both direct component references and configuration objects with optional props and dependencies.
   * Extracts the component reference and creates a computed function that returns the props.
   * 
   * ## Design
   * 
   * Supports two formats:
   * 1. Direct component: `ShadowComponent`
   * 2. Configuration object: `{ component: LightHalo, props: {...}, dependencies: (object) => [...] }`
   * 
   * The normalization process:
   * - Extracts the actual component from either format
   * - Extracts dependencies function if provided
   * - Creates a computed function that returns props (static object or dynamic function result)
   * - Returns a normalized object with `component`, `props`, and `dependencies`
   * 
   * @param comp - Component reference or configuration object
   * @returns Normalized component configuration with component, props, and dependencies
   * 
   * @example
   * ```ts
   * // Direct component
   * normalizeComponent(ShadowComponent)
   * // => { component: ShadowComponent, props: {}, dependencies: undefined }
   * 
   * // With static props
   * normalizeComponent({ component: LightHalo, props: { radius: 30 } })
   * // => { component: LightHalo, props: { radius: 30 }, dependencies: undefined }
   * 
   * // With dynamic props and dependencies
   * normalizeComponent({ 
   *   component: HealthBar, 
   *   props: (object) => ({ hp: object.hp(), maxHp: object.param.maxHp() }),
   *   dependencies: (object) => [object.hp, object.param.maxHp]
   * })
   * // => { component: HealthBar, props: {...}, dependencies: (object) => [...] }
   * ```
   */
  const normalizeComponent = (comp) => {
    let componentRef;
    let propsValue;
    let dependenciesFn;
    
    // If it's a direct component reference
    if (typeof comp === 'function' || (comp && typeof comp === 'object' && !comp.component)) {
      componentRef = comp;
      propsValue = undefined;
      dependenciesFn = undefined;
    }
    // If it's a configuration object with component and props
    else if (comp && typeof comp === 'object' && comp.component) {
      componentRef = comp.component;
      // Support both "data" (legacy) and "props" (new) for backward compatibility
      propsValue = comp.props !== undefined ? comp.props : comp.data;
      dependenciesFn = comp.dependencies;
    }
    // Fallback: treat as direct component
    else {
      componentRef = comp;
      propsValue = undefined;
      dependenciesFn = undefined;
    }
    
    // Return props directly (object or function), not as computed
    // The computed will be created in the template when needed
    return {
      component: componentRef,
      props: typeof propsValue === 'function' ? propsValue(sprite) : propsValue || {},
      dependencies: dependenciesFn ? dependenciesFn(sprite) : []
    };
  };

  /**
   * Normalize an array of sprite components
   * 
   * Applies normalization to each component in the array using `normalizeComponent`.
   * 
   * @param components - Array of component references or configuration objects
   * @returns Array of normalized component configurations
   */
  const normalizeComponents = (components) => {
    return components.map((comp) => normalizeComponent(comp));
  };


  /**
   * Normalized components to render behind sprites
   * Handles both direct component references and configuration objects with optional props and dependencies
   * 
   * Supports multiple formats:
   * 1. Direct component: `ShadowComponent`
   * 2. Configuration object: `{ component: LightHalo, props: {...} }`
   * 3. With dynamic props: `{ component: LightHalo, props: (object) => {...} }`
   * 4. With dependencies: `{ component: HealthBar, dependencies: (object) => [object.hp, object.param.maxHp] }`
   * 
   * Components with dependencies will only be displayed when all dependencies are resolved (!= undefined).
   * The object is passed to the dependencies function to allow sprite-specific dependency resolution.
   * 
   * @example
   * ```ts
   * // Direct component
   * componentsBehind: [ShadowComponent]
   * 
   * // With static props
   * componentsBehind: [{ component: LightHalo, props: { radius: 30 } }]
   * 
   * // With dynamic props and dependencies
   * componentsBehind: [{ 
   *   component: HealthBar, 
   *   props: (object) => ({ hp: object.hp(), maxHp: object.param.maxHp() }),
   *   dependencies: (object) => [object.hp, object.param.maxHp]
   * }]
   * ```
   */
  const normalizedComponentsBehind = computed(() => {
    return normalizeComponents(componentsBehind());
  });

  /**
   * Normalized components to render in front of sprites
   * Handles both direct component references and configuration objects with optional props and dependencies
   * 
   * See `normalizedComponentsBehind` for format details.
   * Components with dependencies will only be displayed when all dependencies are resolved.
   */
  const normalizedComponentsInFront = computed(() => {
    return normalizeComponents(componentsInFront());
  });

  const isEventSprite = () => {
    return typeof sprite?.isEvent === 'function'
      ? sprite.isEvent()
      : sprite?._type === 'event';
  };

  const resolvedEventComponents = computed(() => {
    if (!isEventSprite()) return [];
    const eventComponent = normalizeEventComponent(client.resolveEventComponent(sprite), sprite);
    return eventComponent ? [eventComponent] : [];
  });
  
  /**
   * Determine if the camera should follow this sprite
   * 
   * The camera follows this sprite if:
   * - It's explicitly set as the camera follow target, OR
   * - It's the current player and no explicit target is set (default behavior)
   */
  const shouldFollowCamera = computed(() => {
    const cameraTargetId = client.cameraFollowTargetId();
    // If a target is explicitly set, only follow if this sprite is the target
    if (cameraTargetId !== null) {
      return id() === cameraTargetId;
    }
    // Otherwise, follow the current player (default behavior)
    return isMe();
  });

  /**
   * Get all attached GUI components that should be rendered on sprites
   * These are GUIs with attachToSprite: true
   */
  const attachedGuis = computed(() => {
    return guiService.getAttachedGuis();
  });

  /**
   * Check if attached GUIs should be displayed for this sprite
   * This is controlled by showAttachedGui/hideAttachedGui on the server
   */
  const shouldDisplayAttachedGui = computed(() => {
    return guiService.shouldDisplayAttachedGui(id());
  });

  const { 
    x, 
    y, 
    tint, 
    direction, 
    animationName, 
    animationCurrentIndex,
    emitParticleTrigger, 
    particleName, 
    graphics, 
    hitbox,
    isConnected,
    graphicsSignals,
    flashTrigger
  } = sprite;

  const renderedGraphics = computed(() => {
    const eventComponent = resolvedEventComponents()[0];
    if (eventComponent && !eventComponent.renderGraphic) return [];
    return graphicsSignals();
  });

  /**
   * Flash configuration signals for dynamic options
   * These signals are updated when the flash trigger is activated with options
   */
  const flashType = signal<'alpha' | 'tint' | 'both'>('alpha');
  const flashDuration = signal(300);
  const flashCycles = signal(1);
  const flashAlpha = signal(0.3);
  const flashTint = signal(0xffffff);

  /**
   * Listen to flash trigger to update configuration dynamically
   * When flash is triggered with options, update the signals
   */
  on(flashTrigger, (data) => {
    if (data && typeof data === 'object') {
      if (data.type !== undefined) flashType.set(data.type);
      if (data.duration !== undefined) flashDuration.set(data.duration);
      if (data.cycles !== undefined) flashCycles.set(data.cycles);
      if (data.alpha !== undefined) flashAlpha.set(data.alpha);
      if (data.tint !== undefined) flashTint.set(data.tint);
    }
  });

  /**
   * Flash configuration for the sprite
   * 
   * This configuration is used by the flash directive to create visual feedback effects.
   * The flash trigger is exposed through the object, allowing both server events and
   * client-side code to trigger flash animations. Options can be passed dynamically
   * through the trigger, which updates the reactive signals.
   */
  const flashConfig = computed(() => ({
    trigger: flashTrigger,
    type: flashType(),
    duration: flashDuration(),
    cycles: flashCycles(),
    alpha: flashAlpha(),
    tint: flashTint(),
  }));

  const particleSettings = client.particleSettings;

  const canControls = () => isMe() && getCanMoveValue(sprite) && !sprite.knockbackActive()
  const keyboardControls = client.globalConfig.keyboardControls;
  const movementBindings = movementPreferences(client).bindings;
  const activeDirectionKeys = new Map();
  const activeControlDirections = new Map();
  const activeControlDirectionReleaseTimers = new Map();
  const CONTROL_DIRECTION_RELEASE_GRACE_MS = 160;

  const hasHeldDirection = () =>
    activeDirectionKeys.size > 0 || activeControlDirections.size > 0;

  const publishMobileDebug = (type, payload = {}) => {
    const target = typeof window !== "undefined" ? window : globalThis;
    if (!target.__RPGJS_MOBILE_DEBUG__) return;
    const event = {
      source: "character",
      type,
      time: Date.now(),
      isCurrentPlayer: isCurrentPlayer(),
      animationName: animationName(),
      realAnimationName: realAnimationName?.(),
      activeKeyboardDirections: Array.from(activeDirectionKeys.values()),
      activeControlDirections: Array.from(activeControlDirections.values()),
      heldDirection: resolveHeldDirection(),
      canControls: canControls(),
      x: x(),
      y: y(),
      ...payload
    };
    target.__RPGJS_MOBILE_DEBUG_EVENTS__ = [
      ...(target.__RPGJS_MOBILE_DEBUG_EVENTS__ || []).slice(-199),
      event
    ];
    target.__RPGJS_MOBILE_DEBUG_LAST__ = event;
  };

  const resolveHeldDirection = () => {
    const directions = [
      ...Array.from(activeDirectionKeys.values()),
      ...Array.from(activeControlDirections.values()),
    ];
    return directions[directions.length - 1];
  };

  const resolveSpriteDirection = () => {
    const heldDirection = resolveHeldDirection();
    if (heldDirection) return heldDirection;
    if (typeof sprite.getDirection === 'function') return sprite.getDirection();
    if (typeof sprite.direction === 'function') return sprite.direction();
    return direction();
  };

  const directionToDashVector = (currentDirection) => {
    switch (currentDirection) {
      case Direction.Left:
        return { x: -1, y: 0 };
      case Direction.Right:
        return { x: 1, y: 0 };
      case Direction.Up:
        return { x: 0, y: -1 };
      case Direction.Down:
      default:
        return { x: 0, y: 1 };
    }
  };

  const withCurrentDirection = (payload) => {
    if (payload.action !== 'action') return payload;
    const data = payload.data && typeof payload.data === 'object'
      ? { ...payload.data }
      : {};
    return {
      ...payload,
      data: {
        ...data,
        direction: data.direction ?? resolveSpriteDirection(),
      },
    };
  };

  const resolveCurrentActionInput = () =>
    withCurrentDirection(
      resolveKeyboardActionInput(keyboardControls.action, client, sprite)
    );

  const playPredictedWalkAnimation = () => {
    if (sprite.animationFixed) {
      publishMobileDebug("walk:skip", { reason: "animationFixed" });
      return;
    }
    if (sprite.animationIsPlaying && sprite.animationIsPlaying()) {
      publishMobileDebug("walk:skip", { reason: "animationIsPlaying" });
      return;
    }
    realAnimationName.set('walk');
    publishMobileDebug("walk:set", { reason: "predicted" });
  };

  const resumeHeldDirectionWalkAnimation = () => {
    if (!isCurrentPlayer()) {
      publishMobileDebug("walk:resume-fail", { reason: "notCurrentPlayer" });
      return false;
    }
    if (!hasHeldDirection()) {
      publishMobileDebug("walk:resume-fail", { reason: "noHeldDirection" });
      return false;
    }
    if (!canControls()) {
      publishMobileDebug("walk:resume-fail", { reason: "cannotControl" });
      return false;
    }
    if (sprite.animationFixed) {
      publishMobileDebug("walk:resume-fail", { reason: "animationFixed" });
      return false;
    }
    if (sprite.animationIsPlaying && sprite.animationIsPlaying()) {
      publishMobileDebug("walk:resume-fail", { reason: "animationIsPlaying" });
      return false;
    }
    realAnimationName.set('walk');
    publishMobileDebug("walk:set", { reason: "resumeHeldDirection" });
    return true;
  };

  const processMovementInput = (input) => {
    if (!canControls()) return;
    publishMobileDebug("movement:input", { input });
    client.processInput({ input });
    playPredictedWalkAnimation();
  };

  const activateControlDirection = (name, directionInput) => {
    const releaseTimer = activeControlDirectionReleaseTimers.get(name);
    if (releaseTimer) {
      clearTimeout(releaseTimer);
      activeControlDirectionReleaseTimers.delete(name);
    }
    activeControlDirections.delete(name);
    activeControlDirections.set(name, directionInput);
    publishMobileDebug("control:activate", { name, directionInput });
    resumeHeldDirectionWalkAnimation();
  };

  const releaseControlDirection = (name) => {
    const previousTimer = activeControlDirectionReleaseTimers.get(name);
    if (previousTimer) clearTimeout(previousTimer);
    activeControlDirectionReleaseTimers.set(name, setTimeout(() => {
      activeControlDirectionReleaseTimers.delete(name);
      activeControlDirections.delete(name);
      publishMobileDebug("control:release", { name });
      if (!hasHeldDirection() && animationName() === 'stand') {
        realAnimationName.set('stand');
        publishMobileDebug("stand:set", { reason: "controlRelease" });
      }
    }, CONTROL_DIRECTION_RELEASE_GRACE_MS));
  };

  const processDashInput = () => {
    if (!canControls()) return;
    client.processDash({
      direction: directionToDashVector(resolveSpriteDirection()),
    });
  };

  let lastEscapeActionAt = 0;
  const processEscapeInput = () => {
    const now = Date.now();
    if (now - lastEscapeActionAt < 50) return;
    lastEscapeActionAt = now;
    if (canControls()) {
      client.processAction({ action: 'escape' })
    }
  };

  const actionBind = () => getKeyboardControlBind(keyboardControls.action);
  const keyboardEventId = (event) => `${event.keyCode}:${event.code}:${event.key}`;

  const handleNativeActionWhileMoving = (event) => {
    const inputDirection = resolveKeyboardDirectionInput(event, keyboardControls);
    if (inputDirection) {
      const keyId = keyboardEventId(event);
      if (event.type === 'keydown') {
        activeDirectionKeys.delete(keyId);
        activeDirectionKeys.set(keyId, inputDirection);
        resumeHeldDirectionWalkAnimation();
      }
      else {
        activeDirectionKeys.delete(keyId);
      }
    }

    if (!isCurrentPlayer()) return;
    if (event.type !== 'keydown' || event.repeat) return;
    if (activeDirectionKeys.size === 0) return;
    if (!keyboardEventMatchesBind(event, actionBind())) return;
    if (!canControls()) return;

    client.processAction(resolveCurrentActionInput());
  };

  const visible = computed(() => {
    if (sprite.isEvent()) {
      return true
    }
    return isConnected()
  });

  const controls = computed(() => ({
    down: {
      repeat: true,
      bind: movementBindings().down,
      keyDown() {
        activateControlDirection('down', Direction.Down)
        processMovementInput(Direction.Down)
      },
      keyUp() {
        releaseControlDirection('down')
      },
    },
    up: {
      repeat: true,
      bind: movementBindings().up,
      keyDown() {
        activateControlDirection('up', Direction.Up)
        processMovementInput(Direction.Up)
      },
      keyUp() {
        releaseControlDirection('up')
      },
    },
    left: {
      repeat: true,
      bind: movementBindings().left,
      keyDown() {
        activateControlDirection('left', Direction.Left)
        processMovementInput(Direction.Left)
      },
      keyUp() {
        releaseControlDirection('left')
      },
    },
    right: {
      repeat: true,
      bind: movementBindings().right,
      keyDown() {
        activateControlDirection('right', Direction.Right)
        processMovementInput(Direction.Right)
      },
      keyUp() {
        releaseControlDirection('right')
      },
    },
    action: {
      bind: getKeyboardControlBind(keyboardControls.action),
      keyDown() {
        if (canControls()) {
          client.processAction(resolveCurrentActionInput())
        }
      },
    },
    dash: {
      bind: keyboardControls.dash,
      keyDown() {
        processDashInput()
      },
    },
    back: {
      bind: keyboardControls.escape,
      keyDown() {
        processEscapeInput()
      },
    },
    escape: {
      bind: keyboardControls.escape,
      keyDown() {
        processEscapeInput()
      },
    },
    joystick: {
      enabled: true,
      directionMapping: {
        top: 'up',
        bottom: 'down',
        left: 'left',
        right: 'right',
        top_left: ['up', 'left'],
        top_right: ['up', 'right'],
        bottom_left: ['down', 'left'],
        bottom_right: ['down', 'right']
      },
      moveInterval: 50,
      threshold: 0.1
    },
    gamepad: {
      enabled: true,
      // "back" shares its key with "escape" and is shadowed by it, so target "escape".
      buttonMapping: {
        button_0: "action",
        button_1: "escape",
        button_9: "escape"
      }
    }
  }));

  const recoilSmoothing = new RecoilSmoothing();
  const smoothX = animatedSignal(x(), {
    duration: isMe() ? 0 : 0
  });

  const smoothY = animatedSignal(y(), {
    duration: isMe() ? 0 : 0,
  });

  const z = computed(() => {
    const box = hitbox?.()
    return sprite.y() + (box?.h ?? 0) + sprite.z()
  });

  const realAnimationName = signal(animationName());

  const xSubscription = x.observable.subscribe((value) => {
    smoothX.set(value, { duration: recoilSmoothing.duration(sprite.knockbackActive(), smoothX(), value, Date.now()) });
  });

  const ySubscription = y.observable.subscribe((value) => {
    smoothY.set(value, { duration: recoilSmoothing.duration(sprite.knockbackActive(), smoothY(), value, Date.now()) });
  });
  
  // A frame with a `footAnchor` (the ground point of the character in its cell) is placed so that point
  // sits at the bottom center of the hitbox: the animations of a character (stand, walk...) then keep the
  // feet on the same spot whatever the size of their cells or their scale.
  // The position is in the space of the scaled container, so it comes from the real hitbox and not from
  // `graphicHitbox` (which is in cell pixels and changes with the scale of each animation).
  const alignFootAnchor = (graphicObject) => {
    const box = hitbox();
    const [containerX, containerY] = graphicContainerScale(graphicObject);
    return (frame) => {
      if (!Array.isArray(frame?.footAnchor) || !box || !containerX || !containerY) return frame;
      return { ...frame, anchor: frame.footAnchor, x: box.w / 2 / containerX, y: box.h / containerY };
    };
  };

  const sheet = (graphicObject) => {
    const playback = sprite.animationPlayback?.();
    const playing = playback?.name ?? realAnimationName();
    const definition = {
      ...graphicObject,
      textures: Object.fromEntries(Object.entries(graphicObject.textures ?? {}).map(([name, texture]) => [
        name, {
          ...texture,
          animations: params => {
            const groups = typeof texture.animations === 'function' ? texture.animations(params) : texture.animations;
            const timeline = params.durationMs && groups ? groups.map(frames => fitAnimationTimeline(frames, params.durationMs)) : groups;
            return timeline ? timeline.map(frames => frames.map(alignFootAnchor(graphicObject))) : timeline;
          },
        },
      ])),
    };
    return {
      definition,
      playing,
      params: {
        direction: playback?.direction ?? direction(),
        durationMs: playback?.durationMs
      },
      onFinish() {
        if (sprite.animationPlayback?.() && graphicsSignals().includes(graphicObject)) {
          animationCurrentIndex.update(index => index + 1)
        }
      }
    };
  }

  const graphicScale = (graphicObject) => {
    const scale = graphicObject?.scale;
    if (Array.isArray(scale)) return scale;
    if (typeof scale === 'number') return [scale, scale];
    if (scale && typeof scale === 'object') {
      const x = typeof scale.x === 'number' ? scale.x : 1;
      const y = typeof scale.y === 'number' ? scale.y : x;
      return [x, y];
    }
    return undefined;
  }

  const graphicContainerScale = (graphicObject) => {
    const scale = graphicObject?.displayScale;
    if (Array.isArray(scale)) return scale;
    if (typeof scale === 'number') return [scale, scale];
    if (scale && typeof scale === 'object') {
      const x = typeof scale.x === 'number' ? scale.x : 1;
      const y = typeof scale.y === 'number' ? scale.y : x;
      return [x, y];
    }
    return [1, 1];
  }

  const multiplyScale = (left, right) => {
    const a = normalizePair(left);
    const b = normalizePair(right);
    return [a[0] * b[0], a[1] * b[1]];
  }

  const graphicHitbox = (graphicObject) => {
    const box = hitbox();
    const scale = graphicEffectiveScale(graphicObject);
    return scaleHitboxForGraphicDisplay(box, scale);
  }

  const shadowCaster = (graphicObject) => {
    const box = hitbox();
    const bounds = graphicBounds();
    const height = Math.max(bounds?.height ?? box?.h ?? 32, box?.h ?? 32);

    return {
      enabled: shadowsEnabled,
      height,
      footAnchor: { x: 0.5, y: 1 },
      footOffset: { x: 0, y: 2 },
      alpha: 0.5,
      blur: 3.5,
      gradientPower: 2,
      hardness: 0.42,
      minLength: Math.max(6, (box?.h ?? 32) * 0.25),
      maxLength: Math.max(90, height * 1.8),
      contactAlpha: 0.3,
      contactScale: 0.3,
    };
  }

  const imageDimensions = signal({});
  const loadingImageDimensions = new Set();
  const alphaFrameBounds = signal({});
  const loadingAlphaFrameBounds = new Set();

  const toFiniteNumber = (value, fallback = 0) => {
    const number = typeof value === 'number' ? value : parseFloat(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const clampRatio = (value) => Math.min(1, Math.max(0, value));

  const normalizePair = (value, fallback = [1, 1]) => {
    if (Array.isArray(value)) {
      const x = toFiniteNumber(value[0], fallback[0]);
      const y = toFiniteNumber(value[1] ?? value[0], x);
      return [x, y];
    }
    if (typeof value === 'number') {
      return [value, value];
    }
    if (value && typeof value === 'object') {
      const x = toFiniteNumber(value.x, fallback[0]);
      const y = toFiniteNumber(value.y ?? value.x, x);
      return [x, y];
    }
    return fallback;
  };

  const normalizeAnchor = (value) => {
    if (!Array.isArray(value)) return undefined;
    const [x, y] = normalizePair(value, [0, 0]);
    return [clampRatio(x), clampRatio(y)];
  };

  const resolveImageSource = (image) => {
    if (typeof image === 'string') return image;
    if (typeof image?.default === 'string') return image.default;
    return undefined;
  };

  const parentTextureOptions = (graphicObject) => {
    const props = [
      'width',
      'height',
      'framesHeight',
      'framesWidth',
      'rectWidth',
      'rectHeight',
      'offset',
      'image',
      'sound',
      'spriteRealSize',
      'trimTransparentBounds',
      'scale',
      'anchor',
      'pivot',
      'x',
      'y',
      'opacity'
    ];

    return props.reduce((options, prop) => {
      if (graphicObject?.[prop] !== undefined) {
        options[prop] = graphicObject[prop];
      }
      return options;
    }, {});
  };

  const resolveTextureOptions = (graphicObject) => {
    const textures = graphicObject?.textures ?? {};
    const texture =
      textures[sprite.animationPlayback?.()?.name ?? realAnimationName()] ??
      textures[Animation.Stand] ??
      Object.values(textures)[0] ??
      {};

    return {
      ...parentTextureOptions(graphicObject),
      ...texture
    };
  };

  const resolveAnimationFrames = (textureOptions) => {
    const animations = textureOptions?.animations;
    if (!animations) return [];

    try {
      const frames = typeof animations === 'function'
        ? animations({ direction: sprite.animationPlayback?.()?.direction ?? direction() })
        : animations;
      if (!Array.isArray(frames)) return [];
      const firstGroup = frames[0];
      return (Array.isArray(firstGroup) ? firstGroup : frames)
        .filter((frame) => frame && typeof frame === 'object');
    }
    catch {
      return [];
    }
  };

  const resolveFirstAnimationFrame = (textureOptions) =>
    resolveAnimationFrames(textureOptions)[0] ?? {};

  const optionValue = (prop, frame, textureOptions, graphicObject) => {
    return frame?.[prop] ?? textureOptions?.[prop] ?? graphicObject?.[prop];
  };

  const graphicEffectiveScale = (graphicObject) => {
    const textureOptions = resolveTextureOptions(graphicObject);
    const frame = resolveFirstAnimationFrame(textureOptions);
    const spriteScale = normalizePair(optionValue('scale', frame, textureOptions, graphicObject) ?? graphicScale(graphicObject));
    return multiplyScale(spriteScale, graphicContainerScale(graphicObject));
  };

  const resolveFrameSize = (textureOptions, dimensions) => {
    const framesWidth = toPositiveNumber(textureOptions?.framesWidth) ?? 1;
    const framesHeight = toPositiveNumber(textureOptions?.framesHeight) ?? 1;
    const imageSource = resolveImageSource(textureOptions?.image);
    const loadedSize = imageSource ? dimensions[imageSource] : undefined;
    const fullWidth = toPositiveNumber(textureOptions?.width) ?? loadedSize?.width;
    const fullHeight = toPositiveNumber(textureOptions?.height) ?? loadedSize?.height;
    const width = toPositiveNumber(textureOptions?.rectWidth) ??
      toPositiveNumber(textureOptions?.spriteWidth) ??
      (fullWidth ? fullWidth / framesWidth : undefined);
    const height = toPositiveNumber(textureOptions?.rectHeight) ??
      toPositiveNumber(textureOptions?.spriteHeight) ??
      (fullHeight ? fullHeight / framesHeight : undefined);

    return {
      width,
      height
    };
  };

  const loadImageDimensions = (image) => {
    const source = resolveImageSource(image);
    if (!source || imageDimensions()[source] || loadingImageDimensions.has(source)) {
      return;
    }

    loadingImageDimensions.add(source);
    Assets.load(source)
      .then((texture) => {
        const dimensions = resolveTextureSourceDimensions(texture);
        if (!dimensions) return;

        imageDimensions.update((current) => ({
          ...current,
          [source]: dimensions
        }));
      })
      .catch(() => {})
      .finally(() => {
        loadingImageDimensions.delete(source);
      });
  };

  const alphaBoundsKey = (textureOptions) => {
    const source = resolveImageSource(textureOptions?.image);
    const framesWidth = Math.floor(toPositiveNumber(textureOptions?.framesWidth) ?? 1);
    const framesHeight = Math.floor(toPositiveNumber(textureOptions?.framesHeight) ?? 1);
    return source ? `${source}|${framesWidth}x${framesHeight}` : undefined;
  };

  const readTexturePixels = (texture, width, height) => {
    const resource =
      texture?.source?.resource ??
      texture?.baseTexture?.resource?.source ??
      texture?.source?.resource?.source;
    if (!resource) return undefined;

    const canvas =
      typeof OffscreenCanvas !== 'undefined'
        ? new OffscreenCanvas(width, height)
        : typeof document !== 'undefined'
          ? Object.assign(document.createElement('canvas'), { width, height })
          : undefined;
    const context = canvas?.getContext?.('2d', { willReadFrequently: true });
    if (!context) return undefined;

    context.clearRect(0, 0, width, height);
    context.drawImage(resource, 0, 0, width, height);
    return context.getImageData(0, 0, width, height).data;
  };

  const loadAlphaBounds = (textureOptions) => {
    if (textureOptions?.trimTransparentBounds !== true) return;
    const source = resolveImageSource(textureOptions?.image);
    const key = alphaBoundsKey(textureOptions);
    if (
      !source ||
      !key ||
      Object.prototype.hasOwnProperty.call(alphaFrameBounds(), key) ||
      loadingAlphaFrameBounds.has(key)
    ) {
      return;
    }

    loadingAlphaFrameBounds.add(key);
    loadCachedSpriteSheetAlphaBounds(key, async () => {
      const texture = await Assets.load(source);
      const dimensions = resolveTextureSourceDimensions(texture);
      const framesWidth = Math.floor(toPositiveNumber(textureOptions?.framesWidth) ?? 1);
      const framesHeight = Math.floor(toPositiveNumber(textureOptions?.framesHeight) ?? 1);
      if (!dimensions) return null;
      const { width, height } = dimensions;
      const pixels = readTexturePixels(texture, width, height);
      if (!pixels) return null;
      return scanSpriteSheetAlphaBounds(
        pixels,
        width,
        height,
        framesWidth,
        framesHeight,
      );
    })
      .then((bounds) => {
        alphaFrameBounds.update((current) => ({
          ...current,
          [key]: bounds,
        }));
      })
      .catch(() => {
        // Cross-origin or non-image resources fall back to rectangular bounds.
        alphaFrameBounds.update((current) => ({ ...current, [key]: null }));
      })
      .finally(() => {
        loadingAlphaFrameBounds.delete(key);
      });
  };

  const resolveVisibleFrameBounds = (textureOptions, boundsByKey) => {
    if (textureOptions?.trimTransparentBounds !== true) return undefined;
    const key = alphaBoundsKey(textureOptions);
    const allBounds = key ? boundsByKey[key] : undefined;
    if (!Array.isArray(allBounds)) return undefined;

    const framesWidth = Math.floor(toPositiveNumber(textureOptions?.framesWidth) ?? 1);
    const framesHeight = Math.floor(toPositiveNumber(textureOptions?.framesHeight) ?? 1);
    const frames = resolveAnimationFrames(textureOptions);
    const selectedFrames = frames.length > 0 ? frames : [{ frameX: 0, frameY: 0 }];
    return mergeSpriteAlphaBounds(
      selectedFrames.map((frame) => {
        const frameX = Math.min(
          framesWidth - 1,
          Math.max(0, Math.floor(toFiniteNumber(frame.frameX, 0))),
        );
        const frameY = Math.min(
          framesHeight - 1,
          Math.max(0, Math.floor(toFiniteNumber(frame.frameY, 0))),
        );
        return allBounds[frameY * framesWidth + frameX];
      }),
    );
  };

  effect(() => {
    const sources = new Set();

    graphicsSignals().forEach((graphicObject) => {
      loadAlphaBounds(resolveTextureOptions(graphicObject));
      const baseImage = resolveImageSource(graphicObject?.image);
      if (baseImage) sources.add(baseImage);

      Object.values(graphicObject?.textures ?? {}).forEach((textureOptions) => {
        const image = resolveImageSource(textureOptions?.image ?? graphicObject?.image);
        if (image) sources.add(image);
      });
    });

    sources.forEach((source) => loadImageDimensions(source));
  });

  const hitboxBounds = computed(() => {
    const box = hitbox();
    const width = box?.w ?? 0;
    const height = box?.h ?? 0;

    return {
      left: 0,
      top: 0,
      right: width,
      bottom: height,
      width,
      height,
      centerX: width / 2,
      centerY: height / 2
    };
  });

  const graphicBounds = computed(() => {
    const box = hitbox();
    const fallback = hitboxBounds();
    const customEventComponent = resolvedEventComponents()[0];
    if (customEventComponent && !customEventComponent.renderGraphic) {
      return fallback;
    }
    const dimensions = imageDimensions();
    // Keep this signal read inside the computed so the bounds are recalculated
    // as soon as the asynchronous alpha scan completes.
    const loadedAlphaBounds = alphaFrameBounds();
    const graphics = graphicsSignals();
    let bounds = null;

    graphics.forEach((graphicObject) => {
      const textureOptions = resolveTextureOptions(graphicObject);
      const frame = resolveFirstAnimationFrame(textureOptions);
      const size = resolveFrameSize(textureOptions, dimensions);
      const spriteWidth = size.width ?? box?.w;
      const spriteHeight = size.height ?? box?.h;

      if (!spriteWidth || !spriteHeight) {
        return;
      }

      const scale = graphicEffectiveScale(graphicObject);
      const explicitAnchor = normalizeAnchor(optionValue('anchor', frame, textureOptions, graphicObject));
      const anchor = explicitAnchor ?? resolveScaledHitboxAnchor(
        spriteWidth,
        spriteHeight,
        optionValue('spriteRealSize', frame, textureOptions, graphicObject),
        box,
        scale
      );
      const x = toFiniteNumber(optionValue('x', frame, textureOptions, graphicObject), 0);
      const y = toFiniteNumber(optionValue('y', frame, textureOptions, graphicObject), 0);
      const visible = resolveVisibleFrameBounds(textureOptions, loadedAlphaBounds);
      const graphic = resolveScaledGraphicBounds({
        frameWidth: spriteWidth,
        frameHeight: spriteHeight,
        visibleBounds: visible,
        anchor,
        scale,
        x,
        y,
      });

      bounds = bounds
        ? {
            left: Math.min(bounds.left, graphic.left),
            top: Math.min(bounds.top, graphic.top),
            right: Math.max(bounds.right, graphic.right),
            bottom: Math.max(bounds.bottom, graphic.bottom)
          }
        : graphic;
    });

    if (!bounds) {
      return fallback;
    }

    const width = bounds.right - bounds.left;
    const height = bounds.bottom - bounds.top;

    return {
      ...bounds,
      width,
      height,
      centerX: bounds.left + width / 2,
      centerY: bounds.top + height / 2
    };
  });

  // Internal world-space anchor used by transient sprite effects such as
  // action-battle damage popups. Keeping the computed bounds on the client
  // object lets effects follow graphics that are taller than their hitbox.
  sprite.__rpgjsGraphicBounds = graphicBounds;

  const interactionBounds = () => ({
    bounds: graphicBounds(),
    hitbox: hitboxBounds(),
    graphic: graphicBounds()
  });

  const interactionCursor = computed(() =>
    client.interactions.cursorFor(sprite, interactionBounds())
  );

  const handleInteraction = (type) => (event) => {
    client.updatePointerFromInteractionEvent(event);
    client.interactions.handle(sprite, type, {
      event,
      bounds: interactionBounds()
    });
  };

  const interactionPointerOver = handleInteraction('pointerover');
  const interactionPointerOut = handleInteraction('pointerout');
  const interactionPointerDown = handleInteraction('pointerdown');
  const interactionPointerUp = handleInteraction('pointerup');
  const interactionPointerMove = handleInteraction('pointermove');
  const interactionClick = handleInteraction('click');

  // Combine animation change detection with movement state from smoothX/smoothY
  const epsilon = 0; // movement threshold to consider the easing still running

  const stateX$ = smoothX.animatedState.observable;
  const stateY$ = smoothY.animatedState.observable;
  const animationName$ = animationName.observable;

  const moving$ = combineLatest([stateX$, stateY$]).pipe(
    map(([sx, sy]) => {
      const xFinished = Math.abs(sx.value.current - sx.value.end) <= epsilon;
      const yFinished = Math.abs(sy.value.current - sy.value.end) <= epsilon;
      return !xFinished || !yFinished; // moving if X or Y is not finished
    }),
    startWith(false)
  );

  const animationChange$ = animationName$.pipe(startWith(animationName()));

  let beforeRemovePromise = null;
  let beforeRemoveTransitionValue = null;
  const resolveRemoveContext = () => {
    if (!sprite._removeTransition) return null;
    const value = sprite._removeTransition();
    if (!value || typeof value !== 'string') return null;
    try {
      const context = JSON.parse(value);
      if (!context || typeof context !== 'object' || !context.active) return null;
      context.__transitionValue = value;
      return context;
    }
    catch {
      return null;
    }
  };

  const withTimeout = (promise, timeoutMs = 0) => {
    if (!timeoutMs || timeoutMs <= 0) return promise;
    return Promise.race([
      promise,
      new Promise((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
  };

  const runBeforeRemove = () => {
    const context = resolveRemoveContext();
    if (!context) return Promise.resolve();
    if (beforeRemovePromise && beforeRemoveTransitionValue === context.__transitionValue) {
      return beforeRemovePromise;
    }
    beforeRemoveTransitionValue = context.__transitionValue;
    beforeRemovePromise = withTimeout(
      lastValueFrom(hooks.callHooks("client-sprite-onBeforeRemove", sprite, context)),
      context.timeoutMs
    );
    return beforeRemovePromise;
  };

  const removeTransitionSubscription = sprite._removeTransition?.observable?.subscribe(() => {
    if (resolveRemoveContext()) {
      runBeforeRemove();
    }
  });

  const animationMovementSubscription = combineLatest([
    animationChange$, moving$, sprite.animationPlayback.observable,
    sprite._animationFixed.observable, sprite._canMove.observable, sprite.knockbackActive.observable,
  ]).subscribe(([curr, isMoving, playback]) => {
    realAnimationName.set(resolveCharacterAnimation(
      curr, isMoving, hasHeldDirection(), canControls(), sprite.animationFixed || sprite.knockbackActive(), playback?.name, isCurrentPlayer(),
    ));
  });

  const resumeWalkSubscriptions = [
    sprite.knockbackActive,
    sprite._canMove,
    sprite._animationFixed,
    sprite.animationIsPlaying,
  ]
    .filter(signal => signal?.observable)
    .map(signal => signal.observable.subscribe(() => {
      resumeHeldDirectionWalkAnimation();
    }));

  /**
   * Cleanup subscriptions and call hooks before sprite destruction.
   *
   * # Design
   * - Prevent memory leaks by unsubscribing from all local subscriptions created in this component.
   * - Execute destruction hooks to notify modules and scene map of sprite removal.
   *
   * @example
   * await onBeforeDestroy();
   */
  const waitForTemporaryAnimationEnd = (maxDuration = 1200) => {
    if (!sprite.animationIsPlaying || !sprite.animationIsPlaying()) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      let finished = false;
      let timeout;
      let subscription;
      const finish = () => {
        if (finished) return;
        finished = true;
        clearTimeout(timeout);
        subscription?.unsubscribe();
        resolve();
      };
      timeout = setTimeout(finish, maxDuration);
      subscription = sprite.animationIsPlaying.observable.subscribe((isPlaying) => {
        if (!isPlaying) finish();
      });
      if (finished) subscription.unsubscribe();
    });
  };

  let controlsSubscription: Subscription | null = null;
  const onBeforeDestroy = async () => {
    // Stop publishing controls before asynchronous removal hooks can yield.
    controlsSubscription?.unsubscribe();
    controlsSubscription = null;
    await runBeforeRemove();
    await waitForTemporaryAnimationEnd();
    if (typeof document !== 'undefined') {
      document.removeEventListener('keydown', handleNativeActionWhileMoving);
      document.removeEventListener('keyup', handleNativeActionWhileMoving);
    }
    removeTransitionSubscription?.unsubscribe();
    animationMovementSubscription.unsubscribe();
    resumeWalkSubscriptions.forEach(subscription => subscription.unsubscribe());
    activeControlDirectionReleaseTimers.forEach(timer => clearTimeout(timer));
    activeControlDirectionReleaseTimers.clear();
    xSubscription.unsubscribe();
    ySubscription.unsubscribe();
    await lastValueFrom(hooks.callHooks("client-sprite-onDestroy", sprite)) 
    await lastValueFrom(hooks.callHooks("client-sceneMap-onRemoveSprite", client.sceneMap, sprite))
  }

  mount((element) => {
    let appliedCameraFollowRevision = null;
    let appliedCameraFollowOwnership = null;
    const cameraFollowTarget = {
      get x() {
        return smoothX();
      },
      get y() {
        return smoothY();
      },
      get destroyed() {
        return element.componentInstance?.destroyed ?? true;
      },
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('keydown', handleNativeActionWhileMoving);
      document.addEventListener('keyup', handleNativeActionWhileMoving);
    }
    hooks.callHooks("client-sprite-onAdd", sprite).subscribe()
    hooks.callHooks("client-sceneMap-onAddSprite", client.sceneMap, sprite).subscribe()
    controlsSubscription = observeCurrentPlayerControls(
      isCurrentPlayer,
      element.directives.controls,
      (controls) => client.setKeyboardControls(controls)
    );

    effect(() => {
      const followRevision = client.cameraFollowRevision();
      if (!shouldFollowCamera()) {
        appliedCameraFollowRevision = null;
        appliedCameraFollowOwnership = null;
        return;
      }

      const smoothMove = client.cameraFollowSmoothMove;
      const viewport = element.props.context?.viewport;
      if (!viewport || !element.componentInstance) {
        appliedCameraFollowRevision = null;
        appliedCameraFollowOwnership = null;
        return;
      }

      appliedCameraFollowOwnership = untracked(() =>
        applyCameraFollow({
          viewport,
          target: cameraFollowTarget,
          smoothMove,
          followRevision,
          isCurrentRevision: (revision) => client.cameraFollowRevision() === revision,
          shouldFollowCamera,
          pixelSnap: client.cameraPixelSnap
        })
      );
      appliedCameraFollowRevision = appliedCameraFollowOwnership ? followRevision : null;
    })

    effect(() => {
      if (!shouldFollowCamera() || !ownsCameraFollow(appliedCameraFollowOwnership)) return;
      const viewport = element.props.context?.viewport;
      if (!viewport || !element.componentInstance) return;
      syncCameraFollowPosition(viewport, cameraFollowTarget, client.cameraFollowSmoothMove);
    })

    return () => {
      controlsSubscription?.unsubscribe();
      if (ownsCameraFollowRevision(appliedCameraFollowRevision, client.cameraFollowRevision())) {
        clearCameraFollowPlugins(element.props.context?.viewport, appliedCameraFollowOwnership);
      }
    }
  })

  /**
   * Handle attached GUI finish event
   * 
   * @param gui - The GUI instance
   * @param data - Data passed from the GUI component
   */
  const normalizeOpenId = (value) => {
    const resolved = typeof value === "function" ? value() : value;
    return typeof resolved === "string" && resolved.length > 0 ? resolved : undefined;
  };

  const onAttachedGuiFinish = (gui, data, guiOpenId) => {
    const completedOpenId = normalizeOpenId(guiOpenId);
    const currentOpenId = normalizeOpenId(gui.openId);
    if (completedOpenId && currentOpenId && completedOpenId !== currentOpenId) return;
    guiService.guiClose(gui.name, data, completedOpenId ?? currentOpenId);
  };

  /**
   * Handle attached GUI interaction event
   * 
   * @param gui - The GUI instance
   * @param name - Interaction name
   * @param data - Interaction data
   */
  const onAttachedGuiInteraction = (gui, name, data) => {
    guiService.guiInteraction(gui.name, name, data);
  };

  tick(() => {
    hooks.callHooks("client-sprite-onUpdate").subscribe()
  })
</script>
